import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";

const PX_PER_MM = 96 / 25.4;
const TRANSPORT = {
  desktop: "Desktop app → printer",
  "desktop-legacy": "Desktop app (old version)",
  qz: "QZ Tray → printer",
  browser: "Browser print dialog",
};

function PrintDialog({ mode, info, job, transport, printer, copies, issues, raw, onDone }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onDone(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDone]);

  const pageW = job.widthMm * PX_PER_MM;
  const scale = Math.min(1, 520 / pageW);
  const pageH = (job.heightMm || 297) * PX_PER_MM;
  const warn = issues.filter((i) => i.level === "warn");
  const paper = info.receipt
    ? `${job.widthMm} mm roll × ${job.heightMm} mm long`
    : `${info.preset && info.preset !== "CUSTOM" ? info.preset + " · " : ""}${job.widthMm} × ${job.heightMm} mm${info.landscape ? " · landscape" : " · portrait"}`;

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.45)" }} onMouseDown={() => onDone(false)}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-EAECF0 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-000000">{mode === "preview" ? "Print preview" : "Check before printing"}</h3>
          <button type="button" className="text-667085 text-lg leading-none" onClick={() => onDone(false)}>
            ×
          </button>
        </div>
        <div className="flex flex-1 min-h-0">
          <div className="w-72 shrink-0 border-r border-EAECF0 p-4 flex flex-col gap-3 text-xs text-344054 overflow-auto">
            <div>
              <div className="text-667085">Printer</div>
              <div className="font-semibold text-000000">{printer || "Default printer"}</div>
            </div>
            <div>
              <div className="text-667085">Paper</div>
              <div className="font-semibold text-000000">{paper}</div>
            </div>
            <div>
              <div className="text-667085">Copies</div>
              <div className="font-semibold text-000000">{copies}</div>
            </div>
            <div>
              <div className="text-667085">Sent via</div>
              <div className="font-semibold text-000000">
                {TRANSPORT[transport.kind] || transport.kind}
                {raw ? " (ESC/POS, no driver)" : ""}
              </div>
            </div>
            {issues.length > 0 && (
              <div className="flex flex-col gap-2">
                {issues.map((i, n) => (
                  <div key={n} className={`rounded border p-2 leading-snug ${i.level === "warn" ? "border-F51D00 text-F51D00" : "border-EAECF0 text-344054"}`}>
                    {i.level === "warn" ? "⚠ " : "ℹ "}
                    {i.text}
                  </div>
                ))}
              </div>
            )}
            <a href="/settings/printer-setup" className="text-primary underline mt-auto">
              Printer Setup
            </a>
          </div>
          <div className="flex-1 overflow-auto p-4" style={{ background: "#e5e7eb" }}>
            <div style={{ width: pageW * scale, height: Math.min(pageH, 4000) * scale, margin: "0 auto", background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,.2)", overflow: "hidden" }}>
              <iframe
                title="Print preview"
                srcDoc={job.html}
                scrolling="no"
                style={{ width: pageW, height: Math.min(pageH, 4000), border: 0, transform: `scale(${scale})`, transformOrigin: "top left", pointerEvents: "none" }}
              />
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-EAECF0 flex justify-end gap-2">
          <button type="button" className="border border-EAECF0 text-344054 bg-white text-xs font-medium px-4 py-2 rounded-md" onClick={() => onDone(false)}>
            Cancel
          </button>
          <button type="button" className="bg-primary text-white text-xs font-medium px-4 py-2 rounded-md" onClick={() => onDone(true)} autoFocus>
            {warn.length && mode !== "preview" ? "Print anyway" : `Print${copies > 1 ? ` ${copies} copies` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Shows the dialog anywhere in the app; resolves true (print) or false (cancel).
export function openPrintDialog(props) {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    const done = (go) => {
      root.unmount();
      host.remove();
      resolve(go);
    };
    root.render(<PrintDialog {...props} onDone={done} />);
  });
}
